/** The weapon panel's rows and their key hints, as data (pure: shared by the modern and 1987 HUDs). */

import { keyLabel } from '../../input/bindings';
import type { BindingsPort } from '../../input/bindings';
import type { WeaponId } from '../../sim/types';

export interface WeaponSlotSpec {
  weapon: WeaponId;
  label: string;
  /** Four letters for the 1987 box. */
  short: string;
  /** The binding action that selects it, and the hint shown when the HUD has no bindings. */
  action: string;
  fallbackKey: string;
  /** Rare equipment (04 §6): the row exists only while carried. */
  gear: boolean;
}

/** In panel order, which is also the order next / previous weapon walks. */
export const WEAPON_SLOTS: readonly WeaponSlotSpec[] = [
  { weapon: 'carbine', label: 'CARBINE', short: 'CARB', action: 'weapon1', fallbackKey: '1', gear: false },
  { weapon: 'grenade', label: 'GRENADE', short: 'GREN', action: 'weapon2', fallbackKey: '2', gear: false },
  { weapon: 'law', label: 'LAW', short: 'LAW', action: 'weapon3', fallbackKey: '3', gear: false },
  { weapon: 'bomb', label: 'BOMB', short: 'BOMB', action: 'weapon4', fallbackKey: '4', gear: false },
  { weapon: 'knife', label: 'KNIFE', short: 'KNIF', action: 'weapon5', fallbackKey: '5', gear: false },
  { weapon: 'pistol', label: 'PISTOL', short: 'PIST', action: 'pistol', fallbackKey: '6', gear: true },
  { weapon: 'smoke', label: 'SMOKE', short: 'SMOK', action: 'smoke', fallbackKey: 'Q', gear: true },
];

/** A key cap has room for four characters. */
const HINT_MAX = 4;

export type ActionList = ReturnType<BindingsPort['actions']>;

/** The first key bound to `action`, short enough for a key cap; `fallback` without bindings, blank if unbound. */
export function keyHint(actions: ActionList | null, action: string, fallback: string): string {
  if (actions === null) return fallback;
  const code = actions.find((a) => a.id === action)?.keys[0];
  if (code === undefined) return '';
  const label = keyLabel(code);
  return label.length <= HINT_MAX ? label : label.replace(/\s+/g, '').slice(0, HINT_MAX);
}

/**
 * The original's 2-digit ammunition figure (01 §9): magazines for the carbine, `00` for the
 * knife, the count of everything else.
 */
export function ammoFigure(
  weapon: WeaponId,
  inv: { magazines: number; grenades: number; laws: number; bombs: number },
  gear: { pistolRounds: number; smoke: number },
): string {
  const count = weapon === 'carbine' ? inv.magazines
    : weapon === 'grenade' ? inv.grenades
    : weapon === 'law' ? inv.laws
    : weapon === 'bomb' ? inv.bombs
    : weapon === 'pistol' ? gear.pistolRounds
    : weapon === 'smoke' ? gear.smoke
    : 0;
  return String(Math.min(99, Math.max(0, Math.floor(count)))).padStart(2, '0');
}
